import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAccount, useSwitchChain } from "wagmi";
import BaseImage from "@components/BaseImage";

import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  getLSTOptionsForChain,
  isNumber,
  toFixedWithoutRounding,
  UNSTAKE_NOW,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";

import LstTokenSelect from "@components/LstTokenSelect";
import TicketInput from "@components/TicketInput";
import TicketInputRightSection from "@components/TicketInputRightSection";
import { IoWarningOutline } from "react-icons/io5";

import {
  APPROVE_UNSTAKING_CLICK,
  CONNECT_WALLET_CLICK,
  UNSTAKE_CTA,
} from "@constants/analytics";
import { ERDataType } from "@hooks/useExchangeRate";
import {
  emitReduxEvent,
  formatNumber,
  numberToCurrency,
  trackEvent,
} from "../../utils";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { ETH, STETH } from "@config/contractsConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import useDappConfig from "@hooks/useDappConfig";

import { updateUserData } from "store";
import useUnstakeEstimate from "@hooks/useUnstakeEstimate";
import useUnstakeInfo from "@hooks/useUnstakeInfo";
import useDappChain from "@hooks/useDappChain";
import Tooltip from "@components/Tooltip";

import useAllowance from "@hooks/useAllowance";

const MAX_FRACTION_DIGITS = 4;

interface StakeComponentProps {
  conversionData: ERDataType;
  isActive: boolean;
}

const UnstakeComponent = ({
  conversionData,
  isActive,
}: StakeComponentProps) => {
  const [unstakeAmount, setUnstakeAmount] = useState("");
  const [error, setError] = useState("");

  const { selectedERC20 } = useSelector((state: any) => state.user);

  const isETHWithdrawal = selectedERC20 === ETH;

  const conversions = useMemo(() => {
    const conversionToERC20 = conversionData?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionData?.exchangeRate]);

  const { switchChain } = useSwitchChain();

  const { xTokenAllowance } = useAllowance();

  const { isConnected, address } = useAccount();

  const { xTokenTokenBalance } = useTokenBalance();

  const { openConnectModal } = useConnectModal();

  const { config, isWithdrawSupported, withdrawEnabledChains } =
    useDappConfig();

  const { returns } = useUnstakeEstimate(unstakeAmount);

  const { minAmountToWithdraw, withdrawalableAmount, bufferPoolForAsset } =
    useUnstakeInfo();

  const { chain } = useDappChain();

  const checkInput = useCallback(
    (value = unstakeAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > xTokenTokenBalance) {
        error = `You can't unstake more than available balance in your wallet.`;
      } else if (Number(unstakeAmount) < Number(minAmountToWithdraw)) {
        error = `Maximum withdrawal allowed for ${selectedERC20} ${minAmountToWithdraw}.`;
      } else if (Number(unstakeAmount) > Number(withdrawalableAmount)) {
        error = `Maximum withdrawal allowed for ${selectedERC20} is ${minAmountToWithdraw} .`;
      }
      return error;
    },
    [
      isConnected,
      minAmountToWithdraw,
      selectedERC20,
      unstakeAmount,
      withdrawalableAmount,
      xTokenTokenBalance,
    ]
  );

  useEffect(() => {
    if (unstakeAmount !== "") {
      const error = checkInput(unstakeAmount);
      setError(error);
    }
  }, [address, unstakeAmount, checkInput]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (!isWithdrawSupported) {
      dispatch(
        updateUserData({
          selectedERC20: ETH,
        })
      );
      return;
    }

    const priorityToken = getLSTOptionsForChain(chain.id, true).sort(
      (a: any, b: any) => (a?.unstakePriority || 0) - (b?.unstakePriority || 0)
    )[0];

    if (!priorityToken) {
      dispatch(
        updateUserData({
          selectedERC20: STETH,
        })
      );
      return;
    }

    dispatch(
      updateUserData({
        selectedERC20: priorityToken!.key,
      })
    );
  }, [config.tokens, dispatch, isActive, chain.id]);

  const isInstantWithdraw: boolean = useMemo(() => {
    return +unstakeAmount <= bufferPoolForAsset;
  }, [bufferPoolForAsset, unstakeAmount]);

  const isApproved = useMemo(() => {
    const amount = unstakeAmount || "";
    return (
      xTokenAllowance &&
      Number(xTokenAllowance) &&
      Number(xTokenAllowance) >= Number(amount)
    );
  }, [xTokenAllowance, unstakeAmount]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setUnstakeAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setUnstakeAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else {
      if (!isWithdrawSupported) {
        return `Switch to ${withdrawEnabledChains[0].name} to Unstake`;
      }
      if (isApproved) {
        return UNSTAKE_NOW;
      }
      return APPROVE_CTA;
    }
  };

  const getBannerText = () => {
    if (!isWithdrawSupported) {
      return "Unstake and withdrawals are available on Ethereum. Bridge your assets to Ethereum chain and initiate unstaking";
    } else {
      let extraText = "";
      let daysText = "7-10";
      if (isETHWithdrawal) {
        daysText = "14-16";
        extraText =
          " If funds are available in the withdrawal pool, your request maybe processed in 1-2 days.";
      }
      let msgStr = `Unstake requests usually take ${daysText} days due to the Ethereum network's exit queue and EigenLayer delays.${extraText}`;
      return msgStr;
    }
  };
  const handleClick = useCallback(() => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET_CLICK, {
        cta_location: "center_wiget",
        cta: CONNECT_WALLET_CTA,
      });
    } else {
      if (!isWithdrawSupported) {
        switchChain({ chainId: withdrawEnabledChains[0].id });
      } else if (isApproved) {
        if (!unstakeAmount) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }
        trackEvent(UNSTAKE_CTA, {
          amount: unstakeAmount,
        });

        // emitReduxEvent(EVENTS.HANDLE_UNSTAKE, { amount: unstakeAmount });
        emitReduxEvent(EVENTS.HANDLE_UNSTAKE_GAIN_OPP, {
          nextAction: EVENTS.HANDLE_UNSTAKE,
          amount: unstakeAmount,
        });
        /* isBufferSufficient:
            bufferPoolFor > 0 ? bufferPool > Number(unstakeAmount) : true, */
        setUnstakeAmount("");
      } else {
        // emitReduxEvent(EVENTS.HANDLE_XTOKEN_APPROVE, { amount: unstakeAmount });
        emitReduxEvent(EVENTS.HANDLE_UNSTAKE_GAIN_OPP, {
          nextAction: EVENTS.HANDLE_XTOKEN_APPROVE,
          amount: unstakeAmount,
        });
        trackEvent(APPROVE_UNSTAKING_CLICK, { asset: selectedERC20 });
      }
    }
  }, [
    isConnected,
    openConnectModal,
    isWithdrawSupported,
    isApproved,
    switchChain,
    withdrawEnabledChains,
    unstakeAmount,
    selectedERC20,
    config.tokens,
    config.nativeToken.address,
  ]);

  const handleMaxClick = useCallback(() => {
    setUnstakeAmount(toFixedWithoutRounding(xTokenTokenBalance.toString(), 4));
  }, [xTokenTokenBalance]);

  return (
    <Box pos="relative">
      <Box
        pos="relative"
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        color="textSecondary"
        pointerEvents={isWithdrawSupported || !address ? "all" : "none"}
        filter="auto"
        blur={isWithdrawSupported || !address ? "0" : "3px"}
      >
        <LstTokenSelect
          hideTags
          title="Withdraw rsETH as"
          forUnstake
          rightSection={
            <>
              <Text>Fast withdrawal</Text>
            </>
          }
        />
        <Flex w="100%" alignItems="center">
          <Flex
            justifyContent="space-between"
            flexGrow={1}
            flexDir={{ base: "column", xl: "row" }}
          >
            <Box
              display={{ base: isConnected ? "none" : "inherit", xl: "block" }}
              h="20px"
            >
              <Text>Enter amount of rsETH</Text>
            </Box>
          </Flex>
        </Flex>

        <TicketInput
          inputRightElement={
            <TicketInputRightSection
              isConnected={isConnected}
              tokenAmount={xTokenTokenBalance}
              onClick={handleMaxClick}
              token={config.xtoken.symbol}
            />
          }
          value={unstakeAmount}
          onChange={(value: string) => handleChange(value)}
          error={error}
        />

        {error && (
          <Flex alignItems="center" mt="8px">
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
              {error ? error : "error"}
            </Text>
          </Flex>
        )}

        <Box mt={{ base: "2rem", md: "1.5rem" }}>
          <Flex justifyContent="space-between">
            <Text>You will get</Text>
            <Text>
              {Number(returns)
                ? formatNumber(
                    Number(returns),
                    false,
                    MAX_FRACTION_DIGITS,
                    true
                  )
                : formatNumber(
                    Number(returns),
                    false,
                    MAX_FRACTION_DIGITS,
                    true
                  ) || 0}{" "}
              {selectedERC20}
            </Text>
          </Flex>

          {conversions.toERC20 ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Text>Exchange rate</Text>
              <Text>
                1 {config.xtoken.symbol} ={" "}
                {toFixedWithoutRounding(conversions.toERC20.toString(), 4)}{" "}
                {selectedERC20}
              </Text>
            </Flex>
          ) : null}
          {/* <Flex justifyContent="space-between" mt="1rem">
            <Text>Withdrawal Time</Text>
            <Text>
              {bufferPoolForAsset > 0 &&
              Number(unstakeAmount) > 0 &&
              bufferPoolForAsset > Number(unstakeAmount)
                ? "1-2"
                : isETHWithdrawal
                ? "14-16"
                : "7-10"}
              {" days"}
            </Text>
          </Flex> */}
          {isWithdrawSupported && withdrawalableAmount ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Flex gap="1" alignItems="center">
                <Text>Total available {selectedERC20}</Text>
                <Tooltip
                  closeDelay={300}
                  label={
                    <Text>
                      This is the total amount of asset available with Kelp that
                      can be withdrawn
                    </Text>
                  }
                />
              </Flex>
              <Text>
                {numberToCurrency(withdrawalableAmount.toString())}{" "}
                {selectedERC20}
              </Text>
            </Flex>
          ) : null}
        </Box>
      </Box>
      <Button
        borderRadius="12px"
        bg="textPrimary"
        p="16px 64px"
        h="56px"
        mt="12px"
        width="100%"
        _hover={{
          bg: "textPrimary",
        }}
        onClick={handleClick}
        isDisabled={
          !isWithdrawSupported ? isWithdrawSupported : !!error || !unstakeAmount
        }
      >
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="600"
          color="backgroundCard"
        >
          {getButtonText()}
        </Text>
      </Button>
      {!isInstantWithdraw && (
        <Flex
          mt={2}
          p="12px 16px"
          borderRadius="8px"
          alignItems="center"
          w="100%"
          bg={"warning"}
          color={"textPrimary"}
          _dark={{
            color: "#060710",
          }}
        >
          <Icon as={IoWarningOutline} mr="14px" />
          <Text
            fontWeight="400"
            fontSize={{ base: "12px", lg: "14px" }}
            lineHeight={{ base: "18px", lg: "20px" }}
          >
            {getBannerText()}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default UnstakeComponent;
